import { AxiosResponse } from 'axios';
import axiosInstance from '../../../services/axios/axios';
import {
	DirectReport,
	DirectReportDetails,
	JobCategory,
	JobCategoryDetails,
	OBJ_APP_BRIDGES,
	Supplement,
	PayloadSupplement,
	PayloadValidate,
	PayloadSubmit,
	ProxyResult,
	TrainingTrack,
} from './_constants';

/**
 * Retrieves a user's direct reports, if any.
 *
 * @param {number} id unique identifier of supervisor of interest.
 * @param {number} [systemID=OBJ_APP_BRIDGES.ID] unique identifier of access system of interest.
 * @returns {Promise<AxiosResponse<DirectReport[]>>} array of direct reports assigned to supervisor.
 */
export const getDirectReports = async (id, systemID = OBJ_APP_BRIDGES.ID) => {
	return axiosInstance
		.get('/api/ManagerValidation/DirectReports', {
			params: {
				id: id,
				idSystem: systemID,
			},
		})
		.then(response => {
			if (response?.data?.payload) {
				return response.data.payload.map(
					report => new DirectReport(report),
				);
			}
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Retrieves a user's direct report, if applicable.
 *
 * @param {number} id unique identifier of supervisor of interest.
 * @param {number} reportID unique identifier of direct report of interest.
 * @param {number} [systemID=OBJ_APP_BRIDGES.ID] unique identifier of access system of interest.
 * @param {boolean} readOnly whether to ignore application eligibility before retrieving available data.
 * @returns {Promise<AxiosResponse<DirectReport>>} direct report assigned to supervisor.
 */
export const getDirectReport = async (
	id,
	reportID,
	systemID = OBJ_APP_BRIDGES.ID,
	readOnly = false,
) => {
	return axiosInstance
		.get('/api/ManagerValidation/DirectReport', {
			params: {
				id: id,
				idReport: reportID,
				idSystem: systemID,
				readOnly: readOnly,
			},
		})
		.then(response => {
			return response;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Retrieves a direct report's details, specifically department and job title.
 *
 * @param {number} id unique identifier of direct report.
 * @returns {Promise<AxiosResponse<DirectReportDetails>>} direct report details.
 */
export const getDirectReportDetails = async id => {
	return axiosInstance
		.get('/api/ManagerValidation/DirectReportDetails', {
			params: {
				id: id,
			},
		})
		.then(response => {
			if (response?.data?.payload) {
				return new DirectReportDetails(response.data.payload);
			}
		})
		.catch(ex => {
			throw ex;
		});
};

export const getProxy = async email => {
	return axiosInstance
		.get('/api/ManagerValidation/Proxy', {
			params: {
				email: email,
			},
		})
		.then(response => {
			if (response?.data?.payload) {
				return new ProxyResult(response.data.payload);
			}
		})
		.catch(() => {
			return;
		});
};

/**
 *
 * @param {number} id unique identifier of user of interest.
 * @returns {Promise<AxiosResponse<Object[]>>} array of base direct report objects.
 */
export const getProxies = async (id, levels = 1) => {
	return axiosInstance
		.get('api/ManagerValidation/ManagerProxy/', {
			params: {
				id: id,
				levels: levels,
			},
		})
		.then(response => {
			if (response?.data?.payload) {
				return response.data.payload;
			}
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number} id unique identifer of user submitting validation.
 * @param {DirectReport[]} arrDirectReports direct report's collection of assignments to validate.
 * @param {number=} systemID unique identifier of access system for which validation is being submitted; defaults to Bridges.
 * @param {Supplement[]|null} arrSupplements optional collection of supplements for validated assignments.
 * @returns {Promise<AxiosResponse<string>>} tracking number for request successfully submitted.
 */
export const postSubmit = async (id, arrDirectReports, systemID = OBJ_APP_BRIDGES.ID, arrSupplements = null) => {
	return axiosInstance
		.post(
			'/api/ManagerValidation',
			new PayloadSubmit(
				arrDirectReports.map(
					report =>
						new PayloadValidate({
							userID: id,
							directReport: report,
						}),
				),
				systemID,
				new PayloadSupplement(arrSupplements),
			),
		)
		.then(response => {
			if (response?.data?.payload) {
				return response.data.payload;
			}
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number} jobCategoryID unique identifier of job category of interest.
 * @param {number=} id nullable unique identifier of user of interest.
 * @returns {Promise<AxiosResponse<string[]|null>>} nullable array of additional required supplements for job category of interest.
 */
export const getSupplements = async (jobCategoryID, id = null) => {
	return axiosInstance
		.get('/api/ManagerValidation/Supplements', {
			params: { jobCategoryID: jobCategoryID, id: id },
		})
		.then(response => response?.data?.payload)
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number} jobCategoryID unique identifier of job category of interest.
 * @param {string=} trackingNumber nullable unique identifier of request of interest.
 * @returns {Promise<AxiosResponse<string[]|null>>} nullable array of additional required supplements for job category of interest.
 */
export const getSupplementsByTrackingNumber = async (
	jobCategoryID,
	trackingNumber = '',
) => {
	return axiosInstance
		.get('/api/ManagerValidation/Supplements', {
			params: {
				jobCategoryID: jobCategoryID,
				trackingNumber: trackingNumber,
			},
		})
		.then(response => response?.data?.payload)
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number} id unique identifer of user performing deletion.
 * @param {DirectReport} directReport direct report object being deleted.
 * @returns {Promise<AxiosResponse<boolean>>} success indication.
 */
export const deleteAssignment = async (id, directReport) => {
	return axiosInstance
		.post(
			'/api/ManagerValidation/Delete',
			new PayloadValidate({ userID: id, directReport: directReport }),
		)
		.then(response => response?.status === 204)
		.catch(error => {
			// resource did not yet exist
			if (error?.response?.status === 410) {
				return true;
			}
			throw error;
		});
};

/**
 *
 * @param {string} input
 * @param {number} systemID
 * @returns {Promise<AxiosResponse<JobCategory[]>>}
 */
export const search = async (input, systemID = OBJ_APP_BRIDGES.ID) => {
	return axiosInstance
		.get('/api/ManagerValidation/JobCategorySearch', {
			params: {
				search: input,
				idSystem: systemID,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new JobCategory(el);
				});
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number} id
 * @returns {Promise<AxiosResponse<JobCategoryDetails>>}
 */
export const getJobCategory = async id => {
	return axiosInstance
		.get('/api/ManagerValidation/JobCategories', {
			params: {
				id: id,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return new JobCategoryDetails(response.data.payload);
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Retrieves details for an array of job category unique identifiers in parallel.
 * @param {number[]} arrIds array of job category unique identifiers.
 * @returns {Promise<JobCategoryDetails[]>} array of successfully resolved job category details.
 */
export const getJobCategories = async arrIds => {
	const arrResults = await Promise.allSettled(
		arrIds.map(id => getJobCategory(id)),
	);
	return arrResults
		.filter(result => result.status === 'fulfilled' && result.value)
		.map(result => result.value);
};

/**
 *
 * @param {number} id unique identifier of training track of interest.
 * @returns {Promise<AxiosResponse<TrainingTrack>}
 */
const getTrainingTrack = async id => {
	return axiosInstance
		.get('api/ManagerValidation/TrainingTracks', {
			params: {
				id: id,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return new TrainingTrack(response.data.payload);
			}
			return null;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number[]} arrTrainingTracks array of training track unique identifiers used to retrieve additional information.
 * @param {number=} index index of training track array at which retrievals should start in descending order; used for recursion.
 * @returns {Promise<AxiosResponse<TrainingTrack[]>>}
 */
export const getTrainingTracks = async (
	arrTrainingTracks,
	index = arrTrainingTracks.length - 1,
) => {
	try {
		let arrResponses = [];

		if (index > 0) {
			arrResponses = await getTrainingTracks(
				arrTrainingTracks,
				index - 1,
			);
		}
		const trainingTrack = await getTrainingTrack(arrTrainingTracks[index]);
		if (trainingTrack?.id > -1) {
			arrResponses.push(trainingTrack);
		}
		return arrResponses;
	} catch (ex) {
		throw ex;
	}
};
